import { HttpHeaders, provideHttpClient, withXhr } from '@angular/common/http';
import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { relayStylePagination } from '@apollo/client/utilities';
import { createClient } from '@supabase/supabase-js';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import {
  COMMO_SHARED_DATA_ACCESS_CONFIG,
  COMMO_SHARED_SUPABASE_CLIENT,
  CommoSharedDataAccessConfig,
} from './commo-shared-data-access.config';
import { AuthService } from './services/auth.service';

export function provideCommoSharedDataAccess(
  config: CommoSharedDataAccessConfig,
): EnvironmentProviders {
  const graphqlUrl = `${config.supabaseUrl}/graphql/v1`;

  return makeEnvironmentProviders([
    {
      provide: COMMO_SHARED_DATA_ACCESS_CONFIG,
      useValue: config,
    },
    {
      provide: COMMO_SHARED_SUPABASE_CLIENT,
      useFactory: (cfg: CommoSharedDataAccessConfig) =>
        createClient(cfg.supabaseUrl, cfg.supabaseKey, {
          auth: {
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: true,
          },
        }),
      deps: [COMMO_SHARED_DATA_ACCESS_CONFIG],
    },
    provideHttpClient(withXhr()),
    {
      provide: APOLLO_OPTIONS,
      useFactory: (httpLink: HttpLink, authService: AuthService) => {
        const authLink = new ApolloLink((operation, forward) => {
          const token = authService.session()?.access_token ?? null;
          operation.setContext(
            ({ headers = {} }: { headers?: Record<string, string> }) => ({
              headers: {
                ...headers,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }),
          );
          return forward(operation);
        });

        const httpLinkHandler = httpLink.create({
          uri: graphqlUrl,
          headers: new HttpHeaders({
            apikey: config.supabaseKey,
            'Content-Type': 'application/json',
          }),
        });

        return {
          link: authLink.concat(httpLinkHandler),
          cache: new InMemoryCache({
            dataIdFromObject: (object) =>
              object['nodeId'] ? (object['nodeId'] as string) : undefined,
            typePolicies: {
              Query: {
                fields: {
                  noticesCollection: relayStylePagination([
                    'filter',
                    'first',
                    'after',
                  ]),
                },
              },
            },
          }),
          defaultOptions: {
            watchQuery: {
              errorPolicy: 'all',
              fetchPolicy: 'cache-and-network',
            },
            query: {
              errorPolicy: 'all',
              fetchPolicy: 'network-only',
            },
          },
        };
      },
      deps: [HttpLink, AuthService],
    },
    Apollo,
  ]);
}

export {
  COMMO_SHARED_DATA_ACCESS_CONFIG,
  COMMO_SHARED_SUPABASE_CLIENT,
  type CommoSharedDataAccessConfig,
} from './commo-shared-data-access.config';
